import {
  Box,
  Card,
  EntityAvatar,
  EntityAvatarFallback,
  Icon,
  Text,
  getInitials,
  type BackgroundToken,
  type EntityAvatarSize,
  type IconName,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

// iOS-style segmented control: a muted track with the active segment raised on
// the lighter `background` surface. Clear in any theme (the ToggleGroup's
// selected wash is too subtle on dark surfaces). Active segments are
// chrome-less pressable tiles (`Box as="button"`), the sanctioned non-Button
// pressable pattern.
const ALIGN_SELF_CLASS = {
  start: "self-start",
  center: "self-center",
  end: "self-end",
} as const;

export function Segmented({
  value,
  onChange,
  options,
  alignSelf = "start",
}: Omittable<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; icon?: IconName }[];
  /** Cross-axis placement of the track. `start` (default) hugs the leading
   *  edge in a column; `center` vertically centers it inside a flex row. */
  alignSelf?: "start" | "center" | "end";
}>) {
  return (
    <Box
      direction="row"
      gap="2xs"
      padding="2xs"
      rounded="md"
      surface="card"
      border
      align="center"
      // Box carries no `alignSelf` prop.
      className={ALIGN_SELF_CLASS[alignSelf]}
    >
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <Box
            as="button"
            type="button"
            key={`${o.value}-${i}`}
            direction="row"
            align="center"
            gap="xs"
            paddingX="sm"
            paddingY="2xs"
            rounded="sm"
            background={active ? "background" : undefined}
            hoverBackground={!(active)}
            cursor="pointer"
            onClick={() => onChange(o.value)}
          >
            {o.icon ? (
              <Icon name={o.icon} size="sm" color={active ? undefined : "muted"} />
            ) : null}
            <Text
              size="sm"
              weight={active ? "medium" : "normal"}
              variant={active ? undefined : "muted"}
            >
              {o.label}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

// Summary stat tile: label over a big mono value with optional suffix/color.
export function StatCard({
  label,
  value,
  suffix,
  color,
}: Omittable<{
  label: string;
  value: string;
  suffix?: string;
  color?: "success" | "warning" | "destructive";
}>) {
  return (
    // Card has no `padding`/`fill`/`variant` props — Box supplies them.
    <Card render={<Box padding="md" grow />} radius="lg">
      <Text size="xs" variant="muted">
        {label}
      </Text>
      <Box direction="row" align="baseline" gap="xs" paddingTop="2xs">
        <Text size="2xl" weight="semibold" variant={color} mono>
          {value}
        </Text>
        {suffix ? (
          <Text size="sm" variant="muted">
            {suffix}
          </Text>
        ) : null}
      </Box>
    </Card>
  );
}

// A small colored status dot (service health, flag state).
export function StatusDot({
  background,
}: {
  background: BackgroundToken;
}) {
  return <Box width={8} height={8} rounded="full" background={background} />;
}

/**
 * The severity token every System nav dot reads, decided in one place so two
 * surfaces cannot render different verdicts about the same subject.
 *
 * `known` is the load: green is an affirmative "this is fine" claim, and every
 * count is zero both before the first response and after one that failed, so
 * "nothing is wrong" and "nothing is known" are the same numbers. Neutral until
 * something has actually been read.
 */
export function statusDotToken(
  known: boolean,
  bad: boolean,
  warn: boolean,
): BackgroundToken {
  if (!known) return "muted";
  if (bad) return "destructive";
  return warn ? "warning" : "success";
}

// The 28px muted glyph tile that leads a name/description cell in the admin
// tables.
export function IconTile({ icon }: { icon: IconName }) {
  return (
    <Box
      width={28}
      height={28}
      rounded="md"
      background="muted"
      align="center"
      justify="center"
    >
      <Icon name={icon} size="sm" color="muted" />
    </Box>
  );
}

// Initials avatar for workspace marks — workspaces aren't an entity-chip kind,
// so the entity system can't resolve them. (User/superuser rows go through
// EntityTag.) Uses the entity-group particle's avatar shell, which owns the
// size/shape scale and the default fallback background.
export function InitialsAvatar({
  name,
  size = "default",
  shape = "circle",
}: Omittable<{
  name: string | null | undefined;
  size?: EntityAvatarSize;
  shape?: "circle" | "square";
}>) {
  return (
    <EntityAvatar size={size} shape={shape} alt={name ?? "user"}>
      <EntityAvatarFallback>
        <Text size="xs" weight="bold">
          {getInitials(name)}
        </Text>
      </EntityAvatarFallback>
    </EntityAvatar>
  );
}

// Bordered, rounded surface wrapping a table or panel. `Card` is already a
// bordered rounded surface with no padding of its own, and it keeps its
// `bg-card` fill (DESIGN-TUNABLE).
export function PanelCard({ children }: { children: React.ReactNode }) {
  return (
    <Card radius="lg" className="overflow-hidden">
      {children}
    </Card>
  );
}

// Per-screen header: title + monospace meta line + right-aligned actions.
export function ScreenHeader({
  title,
  meta,
  actions,
}: Omittable<{
  title: string;
  meta?: string;
  actions?: React.ReactNode;
}>) {
  return (
    <Box
      direction="row"
      align="center"
      gap="md"
      paddingX="lg"
      paddingTop="md"
      paddingBottom="sm"
    >
      <Text size="xl" weight="semibold">
        {title}
      </Text>
      {meta ? (
        <Text size="sm" variant="muted" mono>
          {meta}
        </Text>
      ) : null}
      <Box grow />
      {actions ? (
        <Box direction="row" align="center" gap="sm">
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}

// A table header row of uppercase column labels. `cols` are flex weights or
// fixed widths applied to each label cell in order.
export function TableHeaderRow({ children }: { children: React.ReactNode }) {
  return (
    <Box
      direction="row"
      align="center"
      gap="md"
      paddingX="md"
      paddingY="sm"
      surface="card"
      border="bottom"
    >
      {children}
    </Box>
  );
}

export function HeaderLabel({
  children,
  width,
  fill,
  align,
}: Omittable<{
  children: string;
  width?: number;
  fill?: boolean;
  align?: "left" | "right";
}>) {
  return (
    <Box
      width={width}
      fill={fill ? true : undefined}
      align={align === "right" ? "end" : "start"}
    >
      <Text size="2xs" weight="semibold" variant="muted">
        {children.toUpperCase()}
      </Text>
    </Box>
  );
}

// A table body row: the shared layout + bottom divider on every row except the
// last. Pass `onClick` to make it a hoverable, clickable row.
export function TableRow({
  children,
  last,
  onClick,
}: Omittable<{
  children: React.ReactNode;
  last?: boolean;
  onClick?: () => void;
}>) {
  if (onClick) {
    return (
      <Box
        as="button"
        type="button"
        direction="row"
        align="center"
        gap="md"
        paddingX="md"
        paddingY="sm"
        // A `<button>` is shrink-to-fit even as a block-level flex container,
        // so without this a clickable row bounds at its content while the
        // plain-div branch below spans. Pinned here rather than left to the
        // parent's display, so a row always spans wherever it is used.
        fill="width"
        border={last ? undefined : "bottom"}
        hoverBackground
        cursor="pointer"
        onClick={onClick}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      direction="row"
      align="center"
      gap="md"
      paddingX="md"
      paddingY="sm"
      border={last ? undefined : "bottom"}
    >
      {children}
    </Box>
  );
}
